import { Device, DeviceCommandDataModel } from '@/api';

/**
 * The pending (in-flight) config changes and actions for a device, derived from
 * its command queue. Fall/tilt/reboot/power-off have at most one pending change;
 * geofence and wifi are per-slot, so we list every pending slot index.
 */
export type DevicePendingChanges = {
    fall: boolean;
    tilt: boolean;
    reboot: boolean;
    powerOff: boolean;
    geoSlots: number[];
    wifiSlots: number[];
};

export function deriveDevicePendingChanges(
    device: Device
): DevicePendingChanges {
    const pending = (device.commands ?? []).filter(
        (c) => c.status === 'pending'
    );

    const has = (type: string) => pending.some((c) => c.type === type);

    const slots = (
        type: string,
        getSlot: (data: DeviceCommandDataModel) => number | undefined
    ): number[] => {
        const indices = new Set<number>();
        pending.forEach((c) => {
            if (c.type === type) {
                const slot = getSlot(c.data);
                if (slot !== undefined) {
                    indices.add(slot);
                }
            }
        });
        return [...indices].sort((a, b) => a - b);
    };

    return {
        fall: has('set_fall_alert'),
        tilt: has('set_tilt_alert'),
        reboot: has('reboot'),
        powerOff: has('power_off'),
        geoSlots: slots('set_geo_alert', (d) => d.geoAlertIndex),
        wifiSlots: slots('set_wifi', (d) => d.wifiIndex)
    };
}
